import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import {
    ICollectionConfig,
    IConfig,
    InitArguments,
    IQuestsConfig,
    ISnake,
    ITaskConfig,
    IToastConfig,
    IUserState,
    IUserVariables,
    QuestTypes,
    IShortLinkConfig,
    ToastTypes,
    ShortLinkType,
    ShortLinkSubType,
    IRewardState,
    IRawMissionReward,
    lotteryIdsArray,
    LotteryIds,
    ILotteryConfig,
    PlatformType
} from '../types';
import { translationsStore } from 'store/translations';
import * as QUERIES from 'utils/queries';
import { api, Util } from 'utils/oksdk';
import { StorageKeys } from 'utils/storageKeys';

import spin from 'assets/img/spin.png';
import ruby from 'assets/img/ruby-icon.png';
import giftPrize from 'assets/img/wheel-prize-gift.png';
import oksPrize from 'assets/img/wheel-prize-ok.png';
import framePrize from 'assets/img/wheel-prize-frame.png';
import promoPrize from 'assets/img/wheel-prize-promo.png';
import surprizePrize from 'assets/img/wheel-prize-surprize.png';
import toyPrize from 'assets/img/wheel-prize-toy.png';
import Calendar from 'assets/img/quest-list-14day.png';
import Arrow from 'assets/img/smooth-arrow.svg';
import Gift from 'assets/img/gift.png';
import Arrow_left from 'assets/img/smooth-arrow-left.svg';
import ArrowUp from 'assets/img/smooth-arrow-up.svg';
import DragonGift from 'assets/img/dragon-gift.png';
import Ticket from 'assets/img/ticket.png';
import collectionIcon from 'assets/img/collection-icon.png';
import changeIcon from 'assets/img/change-icon.png';
import navTasks from 'assets/img/nav-icon-tasks.png';
import navPresents from 'assets/img/nav-icon-presents.png';
import navHelp from 'assets/img/nav-icon-help.png';
import giftLock from 'assets/img/gift-lock.png';
import present from 'assets/img/presents.png';
import Wheel from 'assets/img/wheel_card.png';
import item1 from 'assets/img/lotteryRewards/item_1.png';
import item2 from 'assets/img/lotteryRewards/item_2.png';
import item3 from 'assets/img/lotteryRewards/item_3.png';
import item4 from 'assets/img/lotteryRewards/item_4.png';
import item5 from 'assets/img/lotteryRewards/item_5.png';
import item6 from 'assets/img/lotteryRewards/item_6.png';
import item7 from 'assets/img/lotteryRewards/item_7.png';
import item8 from 'assets/img/lotteryRewards/item_8.png';
import FullCollection from 'assets/img/full_collection.png';
import TicketBg from 'assets/img/ticket_bg.png';
import mainBottom from 'assets/img/main-page-bottom.png';
import mainBg from 'assets/img/main-background-snake.png';
import OKSDK from '@odnoklassniki/ok-apps-sdk';

export interface IWinnerItem {
    ticketIds: string[];
    modalConfig?: {
        title: string;
        description: string;
        headerImage: string;
        headerBackground: string;
        actionLink: string;
        actionText: string;
        detailsLink?: string;
        detailsText?: string;
    };
}

interface AppSettings {
    linksWithReturnEnabled?: boolean;
    hideOnboarding?: boolean;
    winnersConf?: Record<string, IWinnerItem>;
    winnerTicketConf?: {
        ticketId: string;
        rewardId: string;
        insertParams: Record<string, string>;
        activateLink: string;
    };
}

class Store {
    error: Record<string, string | number> | null = null;
    appParams: InitArguments;
    settingsLoading: boolean = false;
    settings: AppSettings = {};
    catalogConfig?: IConfig = undefined;
    userState?: IUserState = undefined;
    questsConfig?: IQuestsConfig = undefined;
    userVariables?: IUserVariables = undefined;

    loaded: boolean = false;
    loadedPercent: number = 0;

    isWelcome: boolean = false;
    isFirstRun: boolean = false;
    firstDuplicate?: number = undefined;
    isCollectionFinished: boolean = false;
    isMainPartFinished: boolean = true;
    isShowResults: boolean = false;

    currentDay: number = 1;

    isMob: boolean = false;
    isNative: boolean = false;
    isIos: boolean = false;
    isAndroid: boolean = false;
    isAnonym: boolean = true;
    isEnded: boolean = false;
    platform: PlatformType = PlatformType.WEB;

    toastConfig?: IToastConfig = undefined;

    isHightRezMult: number = 1;
    scrollTo: number | undefined = undefined;
    dayToRunGiftAnimation: number | undefined;
    linkToRules = 'https://st.okcdn.ru/static/ny2025-assets/0-0-1/docs-rules.pdf';
    lastLog: Record<string, any> | undefined = undefined;
    inited = false;
    lastOpenedTicket: number | null = null;
    hasInvitableFirends = false;
    mainVideoIsLoaded = false;
    exchangedSnake: ISnake | null = null;
    revolverPrize: IRewardState | null = null;
    isVisited = false;
    shortLinkConfig?: IShortLinkConfig = undefined;
    bgVideoPreloaded?: string = undefined;
    isLoginsQuestJustComleted = false;
    loginsModalReward?: IRewardState;
    lotteryConfig?: Record<LotteryIds, ILotteryConfig> = undefined;
    isMainPage = false;
    checkOverlays = false;
    recievedNorollReward?: IRewardState;

    constructor() {
        this.error = {};
        this.appParams = Util.getRequestParameters() as InitArguments;
        this.appParams.app_id = this.appParams.apiconnection?.split('_')[0] || 512000051618;
        this.isMob = this.appParams.mob === 'true';
        this.platform = this.appParams.mob_platform || PlatformType.WEB;
        this.isIos = this.appParams.mob_platform === PlatformType.APP_IOS;
        this.isAndroid = this.appParams.mob_platform === PlatformType.APP_ANDROID;
        this.isNative = this.isIos || this.isAndroid;
        this.isAnonym = this.appParams.authorized !== '1' && this.appParams.user_name == null;
        this.shortLinkConfig = this.getShortlinkConfig(this.appParams);
        this.setIsVisited(true);
        api.init(this.appParams);

        makeAutoObservable(this, {}, { autoBind: true });
    }

    getShortlinkConfig = (args: InitArguments): IShortLinkConfig | undefined => {
        if (args.custom_args) {
            const customArgs = this.parseCustomArgs(args.custom_args);
            if (customArgs.link) {
                const [linkType, id] = customArgs.link.split('://') as [ShortLinkType, ShortLinkSubType | string];

                return {
                    type: linkType,
                    subtype: linkType === ShortLinkType.TAB ? (id as ShortLinkSubType) : undefined,
                    id: linkType !== ShortLinkType.TAB ? (id as string) : undefined
                };
            }
        }
        return undefined;
    };

    parseCustomArgs = (customArgs: string) => {
        const splited = decodeURIComponent(customArgs).split(/[;,&]/g);
        const parsedArgs = splited.reduce((acc, x) => {
            const [key, value] = x.split('=');
            acc[key] = value;
            return acc;
        }, {} as Record<string, string>);

        return parsedArgs;
    };

    setScrollTo = (index?: number) => {
        this.scrollTo = index;
    };

    setIsHighRez = () => {
        this.isHightRezMult = 2;
    };

    fakePromise = (durationSec: number = 0) => {
        return new Promise<void>((res) => {
            setTimeout(() => res(), durationSec * 1000);
        });
    };

    setFirstDuplicate = (firstDuplicate?: number) => {
        this.firstDuplicate = firstDuplicate;
    };

    setRevolverPrize = (prize: IRewardState | null) => {
        this.revolverPrize = prize;
    };

    setIsVisited(value: boolean) {
        this.isVisited = value;
    }

    setIsLoginsQuestJustComleted(value: boolean) {
        this.isLoginsQuestJustComleted = value;
    }

    setLoginsModalReward(value?: IRewardState) {
        this.loginsModalReward = value;
    }

    setIsMainPage(value: boolean) {
        this.isMainPage = value;
    }

    openLootbox = async (lootbox_id?: number) => {
        if (this.isCollectionFinished || this.isMainPartFinished) {
            return;
        }

        const params: Record<string, string | number> = {
            method: QUERIES.OPEN_LOOTBOX,
            lootbox_id: lootbox_id || -1
        };

        const result = await api.call<IUserState>(params);

        this.setUserState(result);
    };

    fetchInitialConfig = async () => {
        const result = await api.call<IConfig>({
            method: QUERIES.GET_SNAKES
        });

        this.catalogConfig = result;
        this.catalogConfig.collections.forEach((x) => (x.lotteryId = lotteryIdsArray[x.id - 1]));
    };

    fetchAnonymInitialConfig = async () => {
        const result = await api.call<Record<string, string>>({
            method: QUERIES.GET_SETTINGS,
            version: '1.0.0',
            keys: 'anonymSnakesConfig,linksWithReturnEnabled'
        });

        this.catalogConfig = JSON.parse(result.anonymSnakesConfig) as IConfig;
    };

    fetchCustomSettings = async () => {
        const result = await api.call<Record<string, string>>({
            method: QUERIES.GET_SETTINGS,
            version: '1.0.0',
            keys: 'linksWithReturnEnabled,hideOnboarding,winners.conf,ticketToPromo.conf'
        });

        this.settings = {
            linksWithReturnEnabled: result.linksWithReturnEnabled === 'true',
            hideOnboarding: result.hideOnboarding === 'true',
            winnersConf: result['winners.conf'] ? JSON.parse(result['winners.conf']) : undefined,
            winnerTicketConf: result['ticketToPromo.conf'] ? JSON.parse(result['ticketToPromo.conf']) : undefined
        };

        this.isShowResults = !!this.settings.winnersConf;
    };

    getSnakeById(id: number) {
        if (this.catalogConfig) {
            return this.catalogConfig.snakes.find((snake) => snake.id === id);
        }
    }

    getCollectionBySnakeId(id: number) {
        if (this.catalogConfig) {
            let snake: ICollectionConfig | null = null;
            const { collections } = this.catalogConfig;

            for (let i = 0; i < collections.length; i++) {
                const collection = collections[i];
                const item = collection.snakes.find((snakeId) => snakeId === id);

                if (item) {
                    snake = collection;
                    break;
                }
            }

            return snake;
        }
    }

    fetchUserState = async () => {
        const result = await api.call<IUserState>({
            method: QUERIES.GET_USER_STATE
        });

        this.setUserState({ ...result });
    };

    selectSnake = async (snake_id: number) => {
        const result = await api.call<IUserState>({
            method: QUERIES.SELECT_SNAKE,
            snake_id
        });

        await this.loadLottery([this.getCollectionBySnakeId(snake_id)!.lotteryId]);

        const hasFreeSnakes = this.userState?.free_snakes && this.userState.free_snakes.length > 0;
        const haveFreeSnakes = !!result.free_snakes && result.free_snakes.length > 0;

        if (!hasFreeSnakes && haveFreeSnakes) {
            this.setFirstDuplicate(result.free_snakes![0]);
        }

        this.setUserState(result);
    };

    setExchangedSnake(snake: ISnake | null) {
        this.exchangedSnake = snake;
    }

    exchangeSnake = async (snake_id: number) => {
        const result = await api.call<IUserState>({
            method: QUERIES.EXCHANGE_SNAKE,
            snake_id
        });

        this.setUserState(result);

        if (result.exchanged_snake_pairs) {
            const newSnakeId = result.exchanged_snake_pairs[result.exchanged_snake_pairs.length - 1].snake_to;

            this.setExchangedSnake(this.getSnakeById(newSnakeId) ?? null);
        }
    };

    // rollRevolver = async ({ collectionId = '-1', missionId = '' }: RollRevolverParams) => {
    //     const result = await api.call<IPrizeByWheel>({
    //         method: QUERIES.ROLL_REVOLVER,
    //         collection: collectionId,
    //         mission_id: missionId
    //     });

    //     this.logOperation('fortune', 'reward', result.lottery_reward_type + ':' + (result.custom_params.id || 'none'));
    //     this.setRevolverPrize(result);
    //     await this.fetchUserState();
    // };

    rollRevolverCollection = async (collection_id: number) => {
        const collection = this.catalogConfig?.collections.find((x) => x.id === collection_id)!;

        await api.call<any>({
            method: QUERIES.GAME_ACT,
            name: 'executeLottery',
            params: this.lotteryConfig![collection.lotteryId].availableTicket
        });

        await this.loadQuests();
        const reward = this.questsConfig?.rewards.find((x) => x.collection_id === collection_id)!;

        this.logOperation(
            'fortune',
            'reward',
            reward.lottery_reward_type + ':' + 'collection' + ':' + (collection_id || 'none')
        );

        this.setRevolverPrize(reward);

        await this.loadLottery([collection.lotteryId]);
    };

    rollRevolverReward = async ({ mission_id, id, lottery_reward_type }: IRewardState) => {
        if (mission_id) {
            const oldRolled = this.userVariables?.rolled_rewards || '';
            this.logOperation(
                'fortune',
                'reward',
                lottery_reward_type + ':' + 'mission' + ':' + (mission_id || 'none')
            );

            await this.setUserVariable(StorageKeys.ROLLED_REWARDS, oldRolled ? `${oldRolled},${id}` : id + '');
            await this.getUserVariables();
            const reward = this.questsConfig?.rewards.find((x) => x.id === id && x.mission_id === mission_id);
            if (reward) {
                reward.rolled = true;

                this.setRevolverPrize(reward);
            }
        }
    };

    sendNotification = async (reward: IRewardState) => {
        await api.call<any>({
            method: QUERIES.GAME_ACT,
            name: 'notify',
            params: `${reward.renderParams.notificationKey || 'snake_code_lenta14days'},Промокод: ${
                reward.promo_code
            } Ссылка для активации: ${reward.renderParams.activateLink}`
        });
    };

    setUserState = (userState: IUserState) => {
        this.userState = userState;
        this.isCollectionFinished =
            (this.userState.collections &&
                this.catalogConfig?.collections &&
                this.userState.collections.length == this.catalogConfig?.collections.length &&
                this.userState.collections?.every((x) => x.snakes.length === 3)) ??
            false;
    };

    private preloadBG = async () => {
        const res = await fetch('https://st.mycdn.me/static/ny2025-assets/0-0-1/video_bg.mp4');
        const blob = await res.blob();
        this.bgVideoPreloaded = URL.createObjectURL(blob);
    };

    preloadStaticImages = () => {
        return [
            spin,
            ruby,
            giftPrize,
            oksPrize,
            framePrize,
            promoPrize,
            surprizePrize,
            toyPrize,
            Calendar,
            Arrow,
            Gift,
            Arrow_left,
            ArrowUp,
            DragonGift,
            Ticket,
            collectionIcon,
            changeIcon,
            navTasks,
            navPresents,
            navHelp,
            giftLock,
            present,
            Wheel,
            item1,
            item2,
            item3,
            item4,
            item5,
            item6,
            item7,
            item8,
            FullCollection,
            TicketBg,
            mainBottom,
            mainBg
        ].map(this.preloadImage);
    };

    preloadContent = () => {
        const snakesPicturesPromises =
            this.catalogConfig?.snakes.map((x) => this.preloadImage(x.custom_params.img!)) || [];
        const collectionsPictures: Promise<any>[] = [];

        this.catalogConfig?.collections.forEach((x) => {
            if (x.custom_params.img) {
                collectionsPictures.push(this.preloadImage(x.custom_params.img));
            }

            if (x.custom_params.fullImage) {
                collectionsPictures.push(this.preloadImage(x.custom_params.fullImage));
            }
        });

        const promisesBase = [this.fetchTranslations(), ...snakesPicturesPromises, ...collectionsPictures];

        let promises;

        if (!this.isAnonym) {
            const questPicturesPromises =
                this.questsConfig?.tasks.filter((x) => x.img).map((x) => this.preloadImage(x.img!)) || [];

            promises = [
                ...promisesBase,
                this.loadLottery(),
                this.fetchUserState(),
                this.fetchCustomSettings(),
                this.getUserVariables(),
                this.preloadBG(),
                this.fetchInvitableFriends(),
                ...this.preloadStaticImages(),
                ...questPicturesPromises
            ];
        } else {
            promises = promisesBase;
        }

        const resolvedArray = promises.map(() => false);

        promises.forEach((promise, index) => {
            promise.catch(console.warn).finally(() => {
                resolvedArray[index] = true;
                this.loadedPercent = Math.floor((resolvedArray.filter(Boolean).length / resolvedArray.length) * 100);
                if (this.loadedPercent === 100) {
                    this.loaded = true;
                }
            });
        });
    };

    init = () => {
        if (this.loaded) return;

        if (!this.isAnonym) {
            this.closeBanners();
            Promise.allSettled([this.fetchInitialConfig(), this.getUserVariables()])
                .then(this.loadQuests)
                .then(() => this.preloadContent());
        } else {
            Promise.allSettled([this.fetchAnonymInitialConfig()]).then(() => this.preloadContent());
        }
    };

    promiseSeconds = (seconds: number) => new Promise((res) => setTimeout(res, seconds * 1000));

    fetchTranslations = async () => {
        try {
            const result = await api.call<Record<string, string>>({
                method: QUERIES.TRANSLATIONS,
                package: translationsStore.ptsPkg,
                prefix: '',
                keys: '*',
                fields: '*',
                locale: '*'
            });
            runInAction(() => {
                translationsStore.setTranslations(result);
            });
        } catch (e) {
            runInAction(() => {
                console.warn(e);
            });
        }
    };

    fetchInvitableFriends = async () => {
        const result = await api.call<Record<string, string>>({
            method: QUERIES.GET_FRIENDS,
            app_id: this.appParams.app_id
        });

        result?.users.length > 0 && (this.hasInvitableFirends = true);
    };

    preloadImage = (src: string) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(src);
            image.onerror = reject;
            image.src = src;
        });

    closeBanner = async (banner_id: number) => {
        try {
            await api.call<any>({
                method: QUERIES.CLOSE_BANNER,
                banner_link_type: 'GAME_STREAM_BANNER',
                banner_id
            });
        } catch (e) {
            runInAction(() => {
                console.warn(e);
            });
        }
    };

    closeBanners = () => {
        this.closeBanner(3300054);
        this.closeBanner(3300055);
        this.closeBanner(3300056);
    };

    missionRewardToReward = (reward: IRawMissionReward, userVariables?: IUserVariables): IRewardState | null => {
        if (!reward.isNewYear) {
            return null;
        }

        const rolledIDs = userVariables?.rolled_rewards?.split(',') ?? [];

        let rolled = rolledIDs?.includes(reward.logKey || reward.date + '');
        let openImmediately = false;

        if (!rolled && reward.disableRoll) {
            rolled = true;
            openImmediately = true;
            const oldRolled = this.userVariables?.rolled_rewards || '';
            const id = reward.logKey || reward.date + '';
            this.setUserVariable(StorageKeys.ROLLED_REWARDS, oldRolled ? `${oldRolled},${id}` : id + '');
        }

        let promoJson: {
            code?: string;
            activateLink?: string;
        } = {};

        if (reward.opt && reward.opt.startsWith('{') && reward.opt.endsWith('}')) {
            promoJson = JSON.parse(reward.opt);
        }

        const finishedReward: IRewardState = {
            date: reward.date,
            id: reward.logKey || reward.date + '',
            mission_id: reward.missionId,
            collection_id: -1,
            rolled,
            lottery_reward_type: reward.lotteryRewardType,
            renderParams: {
                name: reward.name,
                img: reward.img,
                congrats: reward.congrats,
                desc: reward.desc,
                background: reward.background,
                modalBackground: reward.modalBackground,
                modalTitle: reward.modalTitle,
                modalDescription: reward.modalDescription,
                modalImg: reward.modalImg,
                activateLink: promoJson.activateLink || reward.activateLink,
                activateText: reward.activateText,
                providerLink: reward.providerLink,
                providerText: reward.providerText,
                notificationKey: reward.notificationKey,
                logRender: reward.logRender
            },
            promo_code: promoJson.code || reward.opt
        };

        if (openImmediately) {
            this.setLoginsModalReward(finishedReward);
            if (finishedReward.renderParams.notificationKey) {
                this.sendNotification(finishedReward);
            }
        }

        return finishedReward;
    };

    collectionRewardToReward = (reward: IRawMissionReward): IRewardState | null => {
        if (!reward.isNewYear) {
            return null;
        }

        return {
            date: reward.date,
            id: reward.logKey || reward.date + '',
            mission_id: '',
            collection_id: +(reward.collectionId || '-1'),
            rolled: true,
            lottery_reward_type: reward.lotteryRewardType,
            renderParams: {
                name: reward.name,
                img: reward.img,
                congrats: reward.congrats,
                desc: reward.desc,
                background: reward.background,
                modalBackground: reward.modalBackground,
                modalTitle: reward.modalTitle,
                modalDescription: reward.modalDescription,
                modalImg: reward.modalImg,
                activateLink: reward.activateLink,
                activateText: reward.activateText,
                providerLink: reward.providerLink,
                providerText: reward.providerText,
                logRender: reward.logRender
            },
            promo_code: reward.opt
        };
    };

    loadQuests = async () => {
        const allowedTypes = [QuestTypes.INTERNAL, QuestTypes.INVITE, QuestTypes.LINK, QuestTypes.LOGINS];
        try {
            const questsRaw = await api.call<any>({
                method: QUERIES.GAME_ACT,
                name: 'shop'
            });

            const missionRewards: IRewardState[] =
                questsRaw.history?.mission
                    ?.map((x: IRawMissionReward) => this.missionRewardToReward(x, this.userVariables))
                    .filter(Boolean) ?? [];

            const collectionRewards: IRewardState[] =
                questsRaw.history?.lottery?.map(this.collectionRewardToReward).filter(Boolean) ?? [];

            const rewards = [...collectionRewards, ...missionRewards].sort((a, b) => a.date - b.date);

            this.questsConfig = {
                ...questsRaw,
                rewards,
                tasks: questsRaw.tasks
                    ?.map(
                        (task: Record<string, string>) =>
                            ({ ...task, clientPlatforms: task.clientPlatforms?.split(',') || undefined } as ITaskConfig)
                    )
                    .filter(
                        ({ isNewYearTask, newYearType, clientPlatforms }: ITaskConfig) =>
                            isNewYearTask === 'true' &&
                            newYearType &&
                            allowedTypes.includes(newYearType) &&
                            (!clientPlatforms || clientPlatforms.includes(this.platform))
                    )
            };
        } catch (e) {
            console.warn(e);
        }
    };

    loadLottery = async (ids?: LotteryIds[]) => {
        try {
            const lotteryConfig: Partial<Record<LotteryIds, ILotteryConfig>> = {};
            await Promise.allSettled(
                (ids || lotteryIdsArray).map(
                    (key) =>
                        new Promise((res) => {
                            api.call<any>({
                                method: QUERIES.GAME_ACT,
                                name: 'loadLottery',
                                params: key
                            })
                                .then((conf) => {
                                    lotteryConfig[key] = conf;
                                    res(true);
                                })
                                .catch(console.warn);
                        })
                )
            );

            console.log(lotteryConfig);

            this.lotteryConfig = {
                ...(this.lotteryConfig || {}),
                ...(lotteryConfig as Record<LotteryIds, ILotteryConfig>)
            };
        } catch (e) {
            console.warn(e);
        }
    };

    logOperation = async (
        operation: string,
        eventId: string | number | null,
        data?: Record<string, string> | string | number
    ) => {
        const lastLoggedSnapshot = {
            operation,
            eventId,
            data,
            timestamp: Date.now()
        };

        if (
            this.lastLog &&
            this.lastLog.operation === operation &&
            this.lastLog.eventId === eventId &&
            lastLoggedSnapshot.timestamp - this.lastLog.timestamp < 500
        ) {
            this.lastLog = lastLoggedSnapshot;
            return;
        }

        this.lastLog = lastLoggedSnapshot;

        try {
            await api.call<any>({
                method: QUERIES.LOG_OPERATION,
                operation,
                eventId,
                data: typeof data === 'object' && data !== null ? JSON.stringify(data) : data
            });
        } catch (e) {
            runInAction(() => {
                console.warn(e);
            });
        }
    };

    openLink = (
        link: string,
        isBlank?: boolean,
        shortLinkConfig?: { type: ShortLinkType; subtype: ShortLinkSubType | string }
    ) => {
        const localWindow = window as unknown as { OKApp: any };
        const isInternal = link.includes('/ok.ru') || link.includes('/m.ok.ru');
        let href = link;

        if (this.appParams.mob_platform === 'ios') {
            let prefix;
            if (isInternal) {
                const isInBrowser = link.includes('/game/') || link.includes('/app/');
                prefix = isInBrowser ? 'https://m.ok.ru/apphook/syslink/' : 'https://m.ok.ru/apphook/intlink/';
            } else {
                prefix = 'https://m.ok.ru/apphook/outlink/';
            }

            href = prefix + href;
            window.parent.location.href = href;
            return;
        }

        if (
            localWindow.OKApp &&
            localWindow.OKApp.goToServiceWithReturn &&
            this.isAndroid &&
            this.settings.linksWithReturnEnabled
        ) {
            try {
                const customArgs = shortLinkConfig ? `link=${shortLinkConfig.type}://${shortLinkConfig.subtype}` : '';
                OKSDK.invoke('goToServiceWithReturn', [href, customArgs]);
                return;
            } catch (e) {
                console.warn(e);
            }
        }

        if (isInternal && localWindow.OKApp && localWindow.OKApp.goToService) {
            href = this.isMob ? link.replace('://ok.ru/', '://m.ok.ru/').replace('.web.', '.mob.') : link;
            localWindow.OKApp.goToService(href);
            return;
        } else if (!isInternal && localWindow.OKApp && localWindow.OKApp.openExternalLink) {
            href = this.isMob ? link.replace('://ok.ru/', '://m.ok.ru/').replace('.web.', '.mob.') : link;
            localWindow.OKApp.openExternalLink(href);
            return;
        } else if (!this.isMob && isBlank) {
            const linkEl = document.createElement('a');
            linkEl.href = href;
            linkEl.target = '_blank';
            document.body.appendChild(linkEl);
            linkEl.click();
            linkEl.remove();
            return;
        }

        window.parent.location.href = href;
    };

    setToastConfig = (toastConfig?: IToastConfig) => {
        this.toastConfig = toastConfig;
    };

    showToast = (text: string, type: ToastTypes = ToastTypes.SUCCESS) => {
        this.setToastConfig({ text, type });
    };

    completeQuest = async (missionId: string, goalCount: number = 1) => {
        try {
            const promises = [];
            for (let i = 0; i < goalCount; i++) {
                promises.push(
                    api.call<any>({
                        method: QUERIES.COMPLETE_QUEST,
                        missionId,
                        goalCount: 1
                    })
                );
            }

            await Promise.allSettled(promises);
            return Promise.allSettled([this.loadQuests(), this.fetchUserState()]);
        } catch (e) {
            console.warn(e);
            this.proceedGenericError();
        }
    };

    setUserVariable = async (key: StorageKeys, value: string) => {
        try {
            await api.call<any>({
                method: QUERIES.STORAGE_SET,
                key,
                value
            });

            await this.getUserVariables();
        } catch (e) {
            console.warn(e);
            this.proceedGenericError();
        }
    };

    getUserVariables = async () => {
        try {
            const responce = await api.call<{ data: IUserVariables }>({
                method: QUERIES.STORAGE_GET,
                keys: [
                    StorageKeys.INVITED_USERS,
                    StorageKeys.TAKEN_REWARDS,
                    StorageKeys.VISITED,
                    StorageKeys.QUESTS_SNAPSHOT,
                    StorageKeys.REWARDS_SNAPSHOT,
                    StorageKeys.ROLLED_REWARDS,
                    StorageKeys.END_NOTIFIED
                ].join(',')
            });

            this.userVariables = responce.data;
        } catch (e) {
            console.warn(e);
            this.proceedGenericError();
        }
    };

    private getQuestsSnapshot = () => {
        return this.questsConfig?.tasks.map((x) => x.key).join(';') || '';
    };

    saveQuestsSnapshot = () => {
        return this.setUserVariable(StorageKeys.QUESTS_SNAPSHOT, this.getQuestsSnapshot());
    };

    hasNewQuests = () => {
        if (!this.userVariables?.quests_snapshot) {
            return false;
        }

        const currentSnapshot = this.getQuestsSnapshot();
        const oldSnapshot = this.userVariables.quests_snapshot;

        if (currentSnapshot !== oldSnapshot) {
            const currentSnapshotArr = currentSnapshot.split(';');
            const oldSnapshotArr = oldSnapshot.split(';');
            return currentSnapshotArr.some((x) => !oldSnapshotArr.includes(x));
        }

        return false;
    };

    private getRewardsSnapshot = () => {
        return this.questsConfig?.rewards?.map((x) => x.id).join(';') || '';
    };

    saveRewardsSnapshot = () => {
        return this.setUserVariable(StorageKeys.REWARDS_SNAPSHOT, this.getRewardsSnapshot());
    };

    hasUnopenedRevolvers = () => {
        return this.questsConfig?.rewards?.some((x) => x.rolled === false);
    };

    hasNewRewards = () => {
        if (this.hasUnopenedRevolvers()) {
            return true;
        }

        if (!this.userVariables?.rewards_snapshot) {
            return false;
        }

        const currentSnapshot = this.getRewardsSnapshot();
        const oldSnapshot = this.userVariables.rewards_snapshot;

        if (currentSnapshot !== oldSnapshot) {
            return true;
        }

        return false;
    };

    proceedGenericError = () => {
        this.showToast('Что-то пошло не так, возможно сервер недоступен', ToastTypes.ERROR);
    };

    getVisitedDaysAmount = () => {
        return this.userState ? new Set([...(this.userState?.visited_days || []), this.userState?.day]).size : 0;
    };

    setShortLinkConfig = (config?: IShortLinkConfig) => {
        this.shortLinkConfig = config;
    };

    setCheckOverlays = (checkOverlays: boolean) => {
        this.checkOverlays = checkOverlays;
    };
}

export let store = new Store();

export const StoreContext = createContext<Store>(store);
export const useStoreContext = () => useContext<Store>(StoreContext);
